import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardHeader, 
  CardContent, 
  Button, 
  Typography, 
  Grid,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Box,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  TextField,
  Switch,
  FormControlLabel,
  Tooltip,
  Alert
} from '@mui/material';
import { 
  PlayArrow, 
  Stop, 
  Refresh, 
  Storage, 
  Check, 
  Warning, 
  Person,
  Business,
  HomeWork,
  EventNote,
  BugReport,
  Info
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { ElasticSearchAPI, ElasticStatusResponse } from '../../fetch/elastic-search';
import { useToastContext } from '../../hooks';
import { Page } from '../../components';

const ElasticSearchAdmin: React.FC = () => {
  const { showToast } = useToastContext();
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [status, setStatus] = useState<ElasticStatusResponse | null>(null);
  const [indexingOp, setIndexingOp] = useState<string | null>(null);
  const [selectedEntityType, setSelectedEntityType] = useState<string>('people');
  const [batchSize, setBatchSize] = useState<number>(2000);
  const [debugMode, setDebugMode] = useState<boolean>(false);
  const [togglingDebug, setTogglingDebug] = useState<boolean>(false);

  useEffect(() => {
    fetchStatus();
    
    // Refresh status every minute
    const interval = setInterval(() => {
      fetchStatus(true);
    }, 60000);
    
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, []);

  const fetchStatus = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const response = await ElasticSearchAPI.getStatus();
      setStatus(response);
      setDebugMode(response.debugLoggingEnabled);
    } catch (error) {
      console.error('Error fetching Elasticsearch status:', error);
      showToast('error', 'Failed to fetch Elasticsearch status');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const toggleDebugMode = async () => {
    try {
      setTogglingDebug(true);
      await ElasticSearchAPI.setDebugMode(!debugMode);
      setDebugMode(!debugMode);
      showToast('success', `Debug logging ${!debugMode ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error toggling debug mode:', error);
      showToast('error', 'Failed to toggle debug mode');
    } finally {
      setTogglingDebug(false);
    }
  };

  const startCdcSync = async () => {
    try {
      setLoading(true);
      await ElasticSearchAPI.startCdcSync();
      showToast('success', 'CDC sync service started successfully');
      await fetchStatus();
    } catch (error) {
      console.error('Error starting CDC sync service:', error);
      showToast('error', 'Failed to start CDC sync service');
      setLoading(false);
    }
  };

  const stopCdcSync = async () => {
    try {
      setLoading(true);
      await ElasticSearchAPI.stopCdcSync();
      showToast('success', 'CDC sync service stopped successfully');
      await fetchStatus();
    } catch (error) {
      console.error('Error stopping CDC sync service:', error);
      showToast('error', 'Failed to stop CDC sync service');
      setLoading(false);
    }
  };

  const startIndexing = async (entityType: string = 'all') => {
    try {
      setIndexingOp(entityType);
      
      if (entityType === 'all') {
        await ElasticSearchAPI.reindexAll(batchSize);
      } else {
        await ElasticSearchAPI.indexEntityType(entityType, batchSize);
      }
      
      showToast('success', `Indexing of ${entityType} started successfully`);
    } catch (error) {
      console.error(`Error starting ${entityType} indexing:`, error);
      showToast('error', `Failed to start ${entityType} indexing`);
    } finally {
      setIndexingOp(null);
    }
  };

  const handleBatchSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    if (!isNaN(value) && value > 0) {
      setBatchSize(value);
    }
  };

  const isProduction = status?.cdcService?.isProduction ?? false;
  const environment = status?.cdcService?.environment ?? 'unknown';

  if (loading && !status) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Page title="Elasticsearch Administration">
      {/* Environment Info Banner */}
      {status?.cdcService?.environment && (
        <Box mb={3}>
          <Alert 
            severity={isProduction ? "info" : "warning"}
            icon={<Info />}
          >
            <Box display="flex" alignItems="center">
              <Typography variant="body1" fontWeight="bold" mr={1}>
                Current Environment:
              </Typography>
              <Chip 
                label={environment.toUpperCase()}
                color={isProduction ? "primary" : "default"}
                size="small"
                sx={{ mr: 2 }}
              />
              {isProduction ? (
                <Typography variant="body2">
                  The CDC service is automatically started in production mode and cannot be stopped.
                </Typography>
              ) : (
                <Typography variant="body2">
                  In non-production environments, the CDC service must be manually started.
                </Typography>
              )}
            </Box>
          </Alert>
        </Box>
      )}

      <Grid container spacing={3}>
        {/* CDC Service Status Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader 
              title="CDC Sync Service Status" 
              action={
                <Box display="flex" alignItems="center">
                  {/* Debug Mode Toggle */}
                  <Tooltip title="Toggle verbose logging for debugging">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={debugMode}
                          onChange={toggleDebugMode}
                          color="primary"
                          disabled={togglingDebug}
                          size="small"
                        />
                      }
                      label={
                        <Box display="flex" alignItems="center">
                          <BugReport fontSize="small" sx={{ mr: 0.5 }} />
                          <Typography variant="body2">Debug Mode</Typography>
                        </Box>
                      }
                      sx={{ mr: 2 }}
                    />
                  </Tooltip>
                  <IconButton onClick={() => fetchStatus(true)} disabled={refreshing}>
                    <Refresh />
                  </IconButton>
                </Box>
              }
            />
            <CardContent>
              {refreshing ? (
                <Box display="flex" justifyContent="center" p={2}>
                  <CircularProgress size={30} />
                </Box>
              ) : (
                <>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Chip 
                      icon={status?.cdcService?.isRunning ? <Check /> : <Warning />}
                      label={status?.cdcService?.isRunning ? "Running" : "Stopped"}
                      color={status?.cdcService?.isRunning ? "success" : "error"}
                      sx={{ mr: 2 }}
                    />
                    {status?.cdcService?.startTime && (
                      <Typography variant="body2">
                        Started {formatDistanceToNow(new Date(status.cdcService.startTime))} ago
                      </Typography>
                    )}
                  </Box>
                  
                  <Box display="flex" gap={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<PlayArrow />}
                      onClick={startCdcSync}
                      disabled={status?.cdcService?.isRunning}
                    >
                      Start Service
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      startIcon={<Stop />}
                      onClick={stopCdcSync}
                      disabled={!status?.cdcService?.isRunning || isProduction}
                    >
                      Stop Service
                    </Button>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        {/* Indexing Operations Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Indexing Operations" />
            <CardContent>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Full Reindex
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Storage />}
                    onClick={() => startIndexing('all')}
                    disabled={indexingOp !== null}
                  >
                    {indexingOp === 'all' ? <CircularProgress size={24} /> : 'Reindex All Entities'}
                  </Button>
                </Box>
                
                <Divider />
                
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Entity-Specific Indexing
                  </Typography>
                  
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={5}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Entity Type</InputLabel>
                        <Select
                          value={selectedEntityType}
                          onChange={(e) => setSelectedEntityType(e.target.value)}
                          label="Entity Type"
                        >
                          <MenuItem value="people">
                            <Box display="flex" alignItems="center">
                              <Person fontSize="small" sx={{ mr: 1 }} />
                              People
                            </Box>
                          </MenuItem>
                          <MenuItem value="companies">
                            <Box display="flex" alignItems="center">
                              <Business fontSize="small" sx={{ mr: 1 }} />
                              Companies
                            </Box>
                          </MenuItem>
                          <MenuItem value="properties">
                            <Box display="flex" alignItems="center">
                              <HomeWork fontSize="small" sx={{ mr: 1 }} />
                              Properties
                            </Box>
                          </MenuItem>
                          <MenuItem value="activities">
                            <Box display="flex" alignItems="center">
                              <EventNote fontSize="small" sx={{ mr: 1 }} />
                              Activities
                            </Box>
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Batch Size"
                        type="number"
                        size="small"
                        fullWidth
                        value={batchSize}
                        onChange={handleBatchSizeChange}
                        InputProps={{ inputProps: { min: 100 } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={() => startIndexing(selectedEntityType)}
                        disabled={indexingOp !== null}
                      >
                        {indexingOp === selectedEntityType ? (
                          <CircularProgress size={24} />
                        ) : (
                          'Index'
                        )}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Sync Statistics Table */}
        <Grid item xs={12}>
          <Card>
            <CardHeader title="CDC Sync Statistics" />
            <CardContent>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Table Name</TableCell>
                      <TableCell>Last Synced</TableCell>
                      <TableCell>Last LSN</TableCell>
                      <TableCell align="right">Processed Count</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {status && status.lastSyncStats?.length > 0 ? (
                      status.lastSyncStats.map((stat) => (
                        <TableRow key={stat.tableName}>
                          <TableCell>{stat.tableName}</TableCell>
                          <TableCell>
                          {formatDistanceToNow(new Date(stat.lastSyncedAt))} ago
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {stat.lastLsn || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{stat.processedCount.toLocaleString()}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          No sync statistics available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Page>
  );
};

export default ElasticSearchAdmin;